/**
 * Commodex Table Calculator
 *
 * Builds the big client-facing "Commodex" policy table (RTL Hebrew): months as
 * columns grouped by exchange terminals, with per-derived-material metric rows.
 *
 * Per material (e.g. Cocoa Butter, Cocoa Mass), per month:
 *   demand           D  — ביקוש צפוי         (demand positions)
 *   ratioOrders      R  — הזמנות רטיו          (supply_type = 'ratio_po')
 *   fixedOrders      P  — הזמנות מפוקססות      (supply_type = 'purchase_order'; qty only)
 *   availableInv     I  — מלאי לניצול          (opening stock − emergency buffer, projected)
 *   avgRatio         ρ  — רשיו ממוצע           (qty-weighted PO ratio; fallback = deriving ratio)
 *   toOrder             — חוסרים כמות להזמין    = max(0, D − R − P − I)
 *   toPrice             — חוסרים כמות לתמחר     = toOrder + R
 *   opCoverage          — כיסוי תפעולי          = (R + P + I) / D
 *
 * Available inventory uses the same month-by-month storage roll-forward as the
 * derivation engine / BaseMaterialManagement (the system-wide buffer model):
 *   openingStorage rolls forward: storage = max(0, storage + POs − demand)
 *   emergencyBuffer = baseAmount + safety% × lookahead demand
 *   availableInv    = max(0, openingStorage − emergencyBuffer)
 *
 * Terminal windows (which months each terminal covers) come from the backend
 * derivation allocations — identical to the Terminal Coverage table.
 */

import { TerminalAllocation } from '../../../services/hedgesService';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CommodexMonth {
  key: string;    // `${year}-${month1}`
  year: number;
  month1: number; // 1-based
  label: string;  // e.g. "ינואר"
}

export interface CommodexTerminal {
  code: string;       // futures code, e.g. "H27"
  label: string;      // Hebrew terminal month, e.g. "מרץ"
  monthKeys: string[]; // keys of covered months
}

export interface CommodexCell {
  demand: number;
  ratioOrders: number;
  fixedOrders: number;
  openingInventory: number;
  emergencyBuffer: number;
  availableInventory: number;
  closingInventory: number; // physical stock at month end (rolls to next opening)
  avgRatio: number;      // 0 when undefined
  hasOrders: boolean;    // whether avgRatio came from real POs (vs fallback)
  toOrder: number;
  toPrice: number;
  // Beans-section required quantity = max(0, demand − availableInventory). Unlike
  // toOrder/toPrice above (used for the "לחוזים / להזמין" ordering rows), this does
  // NOT subtract ratioOrders/fixedOrders — PO's now add to the Priced/covered side
  // (בורסה section) instead of reducing what's Required (client request).
  requiredQty: number;
  opCoverage: number | null; // null when demand = 0
}

export interface CommodexMaterialTotals {
  demand: number;
  ratioOrders: number;
  fixedOrders: number;
  toOrder: number;
  toPrice: number;
  avgRatio: number;      // qty-weighted across all POs (fallback if none)
  opCoverage: number | null;
}

export interface CommodexMaterial {
  formulaCode: string;
  formulaName: string;
  fallbackRatio: number;              // deriving-settings ratio (live RIC or constant)
  cells: Record<string, CommodexCell>; // monthKey -> cell
  totals: CommodexMaterialTotals;
}

// ── Beans (פולים) — exchange-contract coverage, terminal level ──
export interface CommodexBeansTerminal {
  code: string;
  requiredContracts: number;    // Σ months in terminal, of max(0, demand−availableInventory)×ratio
  // "כמות חוזים מתומחרים" (priced contracts) = base-formula hedges maturing in
  // terminal + PO's (ratio + fixed) in beans, in lots. PO's used to reduce Required;
  // per client request they now instead add to this covered side.
  onExchangeContracts: number;
  avgPrice: number;             // qty-weighted hedge price
  avgCurrency: string;
  missingContracts: number;     // required − priced (positive = short)
  coveragePct: number | null;   // priced / required (null when required ≤ 0)
  // Financial coverage = 1 − (uncovered beans ÷ bean-equivalent demand), where
  // uncovered beans MT = required×10 − priced×10, and
  // bean-equivalent demand MT = Σ(demand×ratio) across materials + months.
  financialCoveragePct: number | null;
}

export interface CommodexBeans {
  requiredByMonth: Record<string, number>;  // monthKey -> required contracts
  terminals: Record<string, CommodexBeansTerminal>; // keyed by terminal code
  budgetPrice: number;
  totals: {
    requiredContracts: number;
    onExchangeContracts: number;
    avgPrice: number;
    avgCurrency: string;
    missingContracts: number;
    coveragePct: number | null;
    financialCoveragePct: number | null;
  };
}

export interface CommodexData {
  months: CommodexMonth[];
  terminals: CommodexTerminal[];
  materials: CommodexMaterial[];
  beans: CommodexBeans;
}

// ─── Inputs ───────────────────────────────────────────────────────────────────

export interface CommodexDerivedMaterial {
  formulaCode: string;
  formulaName: string;
  priceRatio: number;            // static underlying_per_formula
  linkedRatioCode: string | null; // live ratio RIC code
}

export interface CommodexBufferConfig {
  baseAmount: number;
  lookaheadMonths: number;
  safetyPercentage: number;
}

export interface CommodexPosition {
  formulaCode: string;
  period: string;   // ISO date
  quantity: number;
}

export interface CommodexRatioPosition extends CommodexPosition {
  ratio: number;    // parsed agreed ratio (0 if unknown)
}

/** A base-formula (beans) exchange hedge, for contract-coverage rows. */
export interface CommodexHedge {
  maturityDate: string; // ISO
  quantityMT: number;   // signed by side already, or use `side`
  side: number;         // +1 long, -1 short
  price: number;        // underlying/exchange price
  currency: string;
}

export interface CommodexCalcParams {
  derivedMaterials: CommodexDerivedMaterial[];
  allocations: TerminalAllocation[];
  /** Per-material demand positions. */
  demand: CommodexPosition[];
  /** Per-material on-hand inventory (supply_type = on_hand_inventory). */
  onHand: CommodexPosition[];
  /** Per-material ratio POs (supply_type = ratio_po), with parsed ratio. */
  ratioPOs: CommodexRatioPosition[];
  /** Per-material fixed/priced POs (supply_type = purchase_order); ratio optional. */
  fixedPOs: CommodexRatioPosition[];
  /** Emergency-stock (buffer) config per formula code. */
  bufferConfigs: Record<string, CommodexBufferConfig>;
  /** Live prices map (formula_code -> price) for live ratio resolution. */
  pricesMap: Record<string, number>;
  /** Base-formula (beans) exchange hedges, for the פולים contract-coverage section. */
  hedges?: CommodexHedge[];
  /** User-defined budget price target for beans contracts. */
  beansBudgetPrice?: number;
  /** Reporting currency label for avg prices. */
  baseCurrency?: string;
  /**
   * Frontend-only avg-ratio overrides keyed `${formulaCode}|${monthKey}`. Lets the
   * user "play" with the ratio to see its effect on required contracts. Never stored.
   */
  ratioOverrides?: Record<string, number>;
  /** Limit the table to the first N calendar months (view horizon). 0/undefined = all. */
  horizonMonths?: number;
  /** What-if: move every demand position N calendar months later (negative = earlier). */
  demandShiftMonths?: number;
}

// ICE cocoa futures contract size (metric tons per lot).
const CONTRACT_SIZE_MT = 10;

// ─── Helpers ───────────────────────────────────────────────────────────────────

const HEBREW_MONTHS = [
  'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
];

// Standard futures month codes (F=Jan … Z=Dec).
const MONTH_CODES = ['F', 'G', 'H', 'J', 'K', 'M', 'N', 'Q', 'U', 'V', 'X', 'Z'];

const monthKey = (year: number, month1: number) => `${year}-${month1}`;

/**
 * {year, month1} of an ISO date string, optionally shifted by N calendar months. Reads the
 * YYYY-MM parts directly: `new Date('YYYY-MM-DD')` is UTC midnight, so local getters / setMonth
 * drift a month around DST changes or west of UTC.
 */
function isoMonth(iso: string, shift = 0): { year: number; month1: number } {
  const [y, m] = String(iso).slice(0, 7).split('-').map(Number);
  const idx = y * 12 + (m - 1) + shift;
  return { year: Math.floor(idx / 12), month1: (idx % 12) + 1 };
}
const isoMonthKey = (iso: string, shift = 0) => { const { year, month1 } = isoMonth(iso, shift); return monthKey(year, month1); };

/** Resolve a material's fallback ratio exactly like the derivation engine. */
function resolveFallbackRatio(dm: CommodexDerivedMaterial, pricesMap: Record<string, number>): number {
  const live = dm.linkedRatioCode ? pricesMap[dm.linkedRatioCode] : undefined;
  return live && live > 0 ? live : (Number(dm.priceRatio) || 1);
}

/** Build the list of {year, month1} covered by an allocation's [startDate, endDate]. */
function monthsInAllocation(startDate: string, endDate: string): Array<{ year: number; month1: number }> {
  const out: Array<{ year: number; month1: number }> = [];
  const s = isoMonth(startDate), e = isoMonth(endDate);
  for (let i = s.year * 12 + s.month1 - 1; i <= e.year * 12 + e.month1 - 1; i++) {
    out.push({ year: Math.floor(i / 12), month1: (i % 12) + 1 });
  }
  return out;
}

/** Sum positions into a formula_code -> monthKey -> qty index (optionally month-shifted). */
function indexByMonth(positions: CommodexPosition[], shift = 0): Record<string, Record<string, number>> {
  const idx: Record<string, Record<string, number>> = {};
  for (const p of positions) {
    const key = isoMonthKey(p.period, shift);
    if (!idx[p.formulaCode]) idx[p.formulaCode] = {};
    idx[p.formulaCode][key] = (idx[p.formulaCode][key] || 0) + Math.abs(Number(p.quantity) || 0);
  }
  return idx;
}

/**
 * Index ratio positions as monthKey -> { qty, ratioQty, qtyWithRatio }.
 *   qty          — total order quantity (used for R/P in the shortfall math).
 *   ratioQty     — Σ(qty × ratio), the weighted-ratio numerator.
 *   qtyWithRatio — Σ(qty) counting ONLY positions that carry a ratio (> 0),
 *                  the weighted-ratio denominator. A ratio of 0 is treated as
 *                  missing data (not a real zero) so it never dilutes the avg.
 */
function indexRatios(
  positions: CommodexRatioPosition[],
  firstMonthKey: string | null,
): Record<string, Record<string, { qty: number; ratioQty: number; qtyWithRatio: number }>> {
  const idx: Record<string, Record<string, { qty: number; ratioQty: number; qtyWithRatio: number }>> = {};
  const firstIdx = firstMonthKey ? (([y, m]) => y * 12 + m - 1)(firstMonthKey.split('-').map(Number)) : -Infinity;
  for (const p of positions) {
    const pm = isoMonth(p.period);
    // Overdue orders (delivery before the first table month) are still open supply — snap them
    // into the first month, exactly like the backend derivation / exposure calculator.
    const key = pm.year * 12 + pm.month1 - 1 < firstIdx ? firstMonthKey! : monthKey(pm.year, pm.month1);
    const qty = Math.abs(Number(p.quantity) || 0);
    if (!idx[p.formulaCode]) idx[p.formulaCode] = {};
    if (!idx[p.formulaCode][key]) idx[p.formulaCode][key] = { qty: 0, ratioQty: 0, qtyWithRatio: 0 };
    idx[p.formulaCode][key].qty += qty;
    if (p.ratio > 0) {
      idx[p.formulaCode][key].ratioQty += qty * p.ratio;
      idx[p.formulaCode][key].qtyWithRatio += qty;
    }
  }
  return idx;
}

// ─── Main ───────────────────────────────────────────────────────────────────

export function calculateCommodexTable(params: CommodexCalcParams): CommodexData {
  const {
    derivedMaterials, allocations, demand, onHand, ratioPOs, fixedPOs, bufferConfigs, pricesMap,
    hedges = [], beansBudgetPrice = 4500, baseCurrency = 'USD',
    ratioOverrides = {}, horizonMonths, demandShiftMonths = 0,
  } = params;

  // 1. Build the flat month list + terminal windows from allocations.
  // Allocations can overlap (e.g. a month falling in two terminal windows); guard
  // against duplicate month columns by claiming each calendar month for the FIRST
  // terminal that covers it. Terminals with no unclaimed months are dropped.
  const months: CommodexMonth[] = [];
  const terminals: CommodexTerminal[] = [];
  const seenMonthKeys = new Set<string>();
  for (const alloc of allocations) {
    const allocMonths = monthsInAllocation(alloc.startDate, alloc.endDate);
    if (allocMonths.length === 0) continue;
    const mKeys: string[] = [];
    for (const m of allocMonths) {
      const key = monthKey(m.year, m.month1);
      if (seenMonthKeys.has(key)) continue; // already owned by an earlier terminal
      seenMonthKeys.add(key);
      mKeys.push(key);
      months.push({ key, year: m.year, month1: m.month1, label: HEBREW_MONTHS[m.month1 - 1] });
    }
    if (mKeys.length === 0) continue;
    const end = isoMonth(alloc.endDate);
    terminals.push({
      code: `${MONTH_CODES[end.month1 - 1]}${String(end.year).slice(2)}`,
      // Hebrew month + 2-digit year, e.g. "מרץ '27" — the year used to be legible only
      // via the small futures code below, so make it explicit here too.
      label: `${HEBREW_MONTHS[end.month1 - 1]} '${String(end.year).slice(2)}`,
      monthKeys: mKeys,
    });
  }

  // 1b. Apply the view horizon — keep only the first N calendar months, trimming
  // any terminal that falls entirely beyond it.
  if (horizonMonths && horizonMonths > 0 && months.length > horizonMonths) {
    months.splice(horizonMonths);
    const allowed = new Set(months.map(m => m.key));
    for (let i = terminals.length - 1; i >= 0; i--) {
      terminals[i].monthKeys = terminals[i].monthKeys.filter(k => allowed.has(k));
      if (terminals[i].monthKeys.length === 0) terminals.splice(i, 1);
    }
  }
  const allowedKeys = new Set(months.map(m => m.key));

  // 2. Index positions by month.
  const demandIdx = indexByMonth(demand, demandShiftMonths);
  const firstMonthKey = months[0]?.key ?? null;
  const ratioIdx = indexRatios(ratioPOs, firstMonthKey);
  const fixedIdx = indexRatios(fixedPOs, firstMonthKey);

  // On-hand starting stock per material.
  const onHandStart: Record<string, number> = {};
  for (const p of onHand) {
    onHandStart[p.formulaCode] = (onHandStart[p.formulaCode] || 0) + (Number(p.quantity) || 0);
  }

  // 3. Per material, walk months in order (roll storage forward) and fill cells.
  const orderedKeys = months.map(m => m.key);
  const materials: CommodexMaterial[] = derivedMaterials.map(dm => {
    const fc = dm.formulaCode;
    const fallbackRatio = resolveFallbackRatio(dm, pricesMap);
    const buffer = bufferConfigs[fc] || { baseAmount: 0, lookaheadMonths: 0, safetyPercentage: 0 };

    const cells: Record<string, CommodexCell> = {};
    let storage = onHandStart[fc] || 0;

    // Totals accumulators
    let tDemand = 0, tRatio = 0, tFixed = 0, tToOrder = 0, tToPrice = 0;
    let tRatioQtySum = 0, tRatioWeighted = 0; // for qty-weighted avg ratio across POs
    // Operational-coverage total = demand-weighted average of the monthly ratios,
    // i.e. Σ(R+P+availableInv) / ΣD across months that actually have demand.
    // Prior code summed only (R+P)/ΣD, dropping מלאי לניצול — so the total could sit
    // far below 100% even when every month was above it.
    let tCovNum = 0, tCovDen = 0;

    for (let i = 0; i < months.length; i++) {
      const m = months[i];
      const key = m.key;

      const D = demandIdx[fc]?.[key] || 0;
      const R = ratioIdx[fc]?.[key]?.qty || 0;
      const P = fixedIdx[fc]?.[key]?.qty || 0;

      // Emergency buffer for this month = baseAmount + safety% × lookahead demand.
      let emergency = buffer.baseAmount;
      if (buffer.lookaheadMonths > 0 && buffer.safetyPercentage > 0) {
        let lookahead = 0;
        for (let j = 1; j <= buffer.lookaheadMonths; j++) {
          const fut = new Date(m.year, m.month1 - 1 + j, 1);
          lookahead += demandIdx[fc]?.[monthKey(fut.getFullYear(), fut.getMonth() + 1)] || 0;
        }
        emergency += lookahead * (buffer.safetyPercentage / 100);
      }

      const openingInventory = storage;
      const availableInventory = Math.max(0, openingInventory - emergency);
      const closingInventory = Math.max(0, openingInventory + R + P - D);

      // Weighted-average ratio across this month's POs (ratio + fixed); fallback if none.
      // Denominator counts only qty that actually carries a ratio — a ratio of 0 is
      // missing data, not a real zero, so it must not dilute the average.
      const ratioQtySum = (ratioIdx[fc]?.[key]?.qtyWithRatio || 0) + (fixedIdx[fc]?.[key]?.qtyWithRatio || 0);
      const ratioWeighted = (ratioIdx[fc]?.[key]?.ratioQty || 0) + (fixedIdx[fc]?.[key]?.ratioQty || 0);
      const hasOrders = ratioQtySum > 0;
      const overrideRatio = ratioOverrides[`${fc}|${key}`];
      const avgRatio = overrideRatio !== undefined && overrideRatio > 0
        ? overrideRatio
        : (hasOrders ? ratioWeighted / ratioQtySum : fallbackRatio);

      // Shortfall formulas (user-confirmed) — used for the "לחוזים / להזמין" ordering rows.
      const toOrder = Math.max(0, D - R - P - availableInventory);
      const toPrice = toOrder + R;
      const opCoverage = D > 0 ? (R + P + availableInventory) / D : null;
      // Beans-section required quantity — does NOT subtract PO's (client request).
      const requiredQty = Math.max(0, D - availableInventory);

      cells[key] = {
        demand: D,
        ratioOrders: R,
        fixedOrders: P,
        openingInventory,
        emergencyBuffer: emergency,
        availableInventory,
        closingInventory,
        avgRatio,
        hasOrders,
        toOrder,
        toPrice,
        requiredQty,
        opCoverage,
      };

      // Roll storage forward: consumed by demand, replenished by POs.
      storage = closingInventory;

      // Accumulate totals.
      tDemand += D; tRatio += R; tFixed += P; tToOrder += toOrder; tToPrice += toPrice;
      tRatioQtySum += ratioQtySum; tRatioWeighted += ratioWeighted;
      if (D > 0) { tCovNum += R + P + availableInventory; tCovDen += D; }
    }

    void orderedKeys;
    const totals: CommodexMaterialTotals = {
      demand: tDemand,
      ratioOrders: tRatio,
      fixedOrders: tFixed,
      toOrder: tToOrder,
      toPrice: tToPrice,
      avgRatio: tRatioWeighted > 0 ? tRatioWeighted / tRatioQtySum : fallbackRatio,
      opCoverage: tCovDen > 0 ? tCovNum / tCovDen : null,
    };

    return { formulaCode: fc, formulaName: dm.formulaName, fallbackRatio, cells, totals };
  });

  // 4. Beans (פולים) — exchange-contract coverage.
  // Required contracts per month = Σ_material(requiredQty × avgRatio) / contract size.
  // Does NOT subtract PO's — see requiredQty on CommodexCell (client request).
  const requiredByMonth: Record<string, number> = {};
  // PO's per month, in beans (ratioOrders + fixedOrders) × avgRatio — these now ADD
  // to the Priced/covered side (בורסה section) instead of reducing Required.
  const posBeansByMonth: Record<string, number> = {};
  // Bean-equivalent demand per month (Σ demand×ratio, MT) — denominator of financial coverage.
  const beansDemandByMonth: Record<string, number> = {};
  for (const m of months) {
    let beans = 0;
    let posBeans = 0;
    let beansDemand = 0;
    for (const mat of materials) {
      const c = mat.cells[m.key];
      if (c) {
        beans += c.requiredQty * c.avgRatio;
        posBeans += (c.ratioOrders + c.fixedOrders) * c.avgRatio;
        beansDemand += c.demand * c.avgRatio;
      }
    }
    requiredByMonth[m.key] = beans / CONTRACT_SIZE_MT;
    posBeansByMonth[m.key] = posBeans;
    beansDemandByMonth[m.key] = beansDemand;
  }

  // Per-terminal contract coverage: match base-formula hedges by maturity to the window.
  const beansTerminals: Record<string, CommodexBeansTerminal> = {};
  let prevEnd: Date | null = null;
  let totOnExchange = 0, totRequired = 0, totPriceNum = 0, totPriceDen = 0, totBeansDemand = 0;
  const currencies = new Set<string>();

  const beansSeenKeys = new Set<string>();
  for (const alloc of allocations) {
    const allocMonths = monthsInAllocation(alloc.startDate, alloc.endDate);
    if (allocMonths.length === 0) continue;
    const endM = isoMonth(alloc.endDate);
    const code = `${MONTH_CODES[endM.month1 - 1]}${String(endM.year).slice(2)}`;
    const end = new Date(alloc.endDate);
    // Claim each month for the first terminal only — mirrors the dedup used when
    // building the month columns so required-contracts never double-counts.
    const mKeys: string[] = [];
    for (const m of allocMonths) {
      const key = monthKey(m.year, m.month1);
      if (beansSeenKeys.has(key)) continue;
      beansSeenKeys.add(key);
      if (!allowedKeys.has(key)) continue; // beyond the view horizon
      mKeys.push(key);
    }
    if (mKeys.length === 0) continue;

    const requiredContracts = mKeys.reduce((s, k) => s + (requiredByMonth[k] || 0), 0);
    const posContracts = mKeys.reduce((s, k) => s + (posBeansByMonth[k] || 0), 0) / CONTRACT_SIZE_MT;

    // Hedges maturing within (prevEnd, end].
    let onExchangeMT = 0;
    let priceNum = 0, priceDen = 0;
    for (const h of hedges) {
      if (!h.maturityDate) continue;
      const d = new Date(h.maturityDate);
      if (prevEnd !== null && d <= prevEnd) continue;
      if (d > end) continue;
      const signedMT = (Number(h.quantityMT) || 0) * (h.side < 0 ? -1 : 1);
      onExchangeMT += signedMT;
      const qty = Math.abs(Number(h.quantityMT) || 0);
      if (h.price > 0 && qty > 0) { priceNum += qty * h.price; priceDen += qty; currencies.add(h.currency); }
    }
    // "כמות חוזים מתומחרים" (priced contracts) = exchange hedges + PO's (client request).
    const onExchangeContracts = onExchangeMT / CONTRACT_SIZE_MT + posContracts;
    const avgPrice = priceDen > 0 ? priceNum / priceDen : 0;

    // Financial coverage: 1 − (uncovered beans MT ÷ bean-equivalent demand MT).
    const beansDemandMT = mKeys.reduce((s, k) => s + (beansDemandByMonth[k] || 0), 0);
    const uncoveredMT = requiredContracts * CONTRACT_SIZE_MT - onExchangeContracts * CONTRACT_SIZE_MT;
    const financialCoveragePct = beansDemandMT > 0 ? 1 - uncoveredMT / beansDemandMT : null;

    beansTerminals[code] = {
      code,
      requiredContracts,
      onExchangeContracts,
      avgPrice,
      avgCurrency: baseCurrency,
      missingContracts: requiredContracts - onExchangeContracts,
      coveragePct: requiredContracts > 0 ? onExchangeContracts / requiredContracts : null,
      financialCoveragePct,
    };

    totRequired += requiredContracts;
    totOnExchange += onExchangeContracts;
    totBeansDemand += beansDemandMT;
    totPriceNum += priceNum; totPriceDen += priceDen;
    prevEnd = end;
  }

  const beans: CommodexBeans = {
    requiredByMonth,
    terminals: beansTerminals,
    budgetPrice: beansBudgetPrice,
    totals: {
      requiredContracts: totRequired,
      onExchangeContracts: totOnExchange,
      avgPrice: totPriceDen > 0 ? totPriceNum / totPriceDen : 0,
      avgCurrency: currencies.size === 1 ? Array.from(currencies)[0] : baseCurrency,
      missingContracts: totRequired - totOnExchange,
      coveragePct: totRequired > 0 ? totOnExchange / totRequired : null,
      financialCoveragePct: totBeansDemand > 0
        ? 1 - ((totRequired - totOnExchange) * CONTRACT_SIZE_MT) / totBeansDemand
        : null,
    },
  };

  return { months, terminals, materials, beans };
}
